import React, { PropsWithChildren, ReactNode } from 'react'
import clsx from 'clsx'
import { AnalyticsElement } from '@typesApp/Analytics'
import { injectAnalytics } from '@utils/analyticsAttributes'
import { injectDataAttributes } from '@utils/dataAttributes'
import styles from './styles/Drawer.module.scss'
import { SVGIcon } from '../SVGIcon/SVGIcon'

interface BaseProps extends PropsWithChildren {
  className?: string
}

interface DrawerProps extends BaseProps {
  open?: boolean
  hideCloseButton?: boolean
  topHeader?: ReactNode
  onClose?: () => void
}

export const Drawer: React.FC<DrawerProps> = props => {
  const { open, onClose, hideCloseButton, topHeader, className, children } = props

  // prevent background scrolling when drawer is open
  if (open) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }

  const vh = document.documentElement.clientHeight * 0.01

  const dataAttributes = {
    ...injectDataAttributes('app.drawer'),
    ...(open ? injectDataAttributes('app.drawerOpen') : undefined),
  }

  return (
    <div className={clsx(styles.backdrop, open && styles.backdropOpen)} style={{ '--vh': `${vh}px` }}>
      <div className={clsx(styles.drawer, open && styles.drawerOpen, className)} {...dataAttributes}>
        <div className={styles.content}>
          {(!hideCloseButton || topHeader) && (
            <div className={clsx(styles.topHeader, topHeader && styles.withContent)}>
              {topHeader}
              {!hideCloseButton && (
                <button
                  className={styles.closeButton}
                  onClick={onClose}
                  {...injectDataAttributes('app.drawerClose')}
                  {...injectAnalytics(AnalyticsElement.CLOSE)}
                >
                  <SVGIcon name="close" />
                </button>
              )}
            </div>
          )}
          {open && children}
        </div>
      </div>
    </div>
  )
}

interface DrawerContentHeaderProps extends BaseProps {
  title?: ReactNode
  description?: ReactNode
}

export const DrawerContentHeader: React.FC<DrawerContentHeaderProps> = props => {
  const { className, title, description, children } = props

  return (
    <div
      className={clsx(styles.contentHeader, title && !description && styles.titleOnly, className)}
      {...injectDataAttributes('app.drawerHeader')}
    >
      {typeof title === 'string' ? <h2 className={styles.headerTitle}>{title}</h2> : title}
      {typeof description === 'string' ? <p className={styles.headerDescription}>{description}</p> : description}
      {children}
    </div>
  )
}

export const DrawerContentBody: React.FC<BaseProps> = props => {
  const { className, children } = props

  return (
    <div className={clsx(styles.contentBody, className)} {...injectDataAttributes('app.drawerContent')}>
      {children}
    </div>
  )
}

type DrawerContentFooterProps = BaseProps & {
  noGutter?: boolean
}

export const DrawerContentFooter: React.FC<DrawerContentFooterProps> = props => {
  const { noGutter, className, children } = props

  return (
    <div
      className={clsx(styles.contentFooter, noGutter && styles.noGutter, className)}
      {...injectDataAttributes('app.drawerFooter')}
    >
      {children}
    </div>
  )
}
